import React from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CartItemCard from './CartItemCard';

interface CartItemsListProps {
    items: any[] | undefined; // Using any due to incomplete type definitions
    onRemoveItem: (id: number) => void;
    onRemoveAll: () => void;
    onItemExpired?: (itemId: number, eventName: string, childName: string) => void;
    loading: boolean;
}

interface GroupedItems {
    [key: string]: {
        childName: string;
        items: any[];
    };
}

const CartItemsList: React.FC<CartItemsListProps> = ({ items, onRemoveItem, onRemoveAll, onItemExpired, loading }) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!items || items.length === 0) {
        return (
            <Card
                elevation={2}
                sx={{
                    borderLeft: '4px solid #88B73E',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minHeight: '460px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CardContent sx={{ py: 9, px: 3.5, textAlign: 'center' }}>
                    <ShoppingCartOutlinedIcon 
                        sx={{ 
                            fontSize: 72, 
                            color: 'text.secondary', 
                            mb: 2.5,
                            opacity: 0.5 
                        }} 
                    />
                    <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 500, mb: 1.5, fontSize: '1.35rem' }}>
                        Your cart is empty
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                        Browse events and add items to get started
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Group items by child
    const groupedByChild: GroupedItems = items.reduce((acc: GroupedItems, item) => {
        const childKey = `${item.child?.firstName}_${item.child?.lastName}_${item.child?.id}`;
        if (!acc[childKey]) {
            acc[childKey] = {
                childName: `${item.child?.firstName} ${item.child?.lastName}`,
                items: [],
            };
        }
        acc[childKey].items.push(item);
        return acc;
    }, {});

    return (
        <Box>
            {/* Child Groups */}
            {Object.values(groupedByChild).map((group) => (
                <Box key={group.childName} sx={{ mb: 3.5 }}>
                    {/* Child Name Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                        <ChildCareIcon sx={{ fontSize: 28, color: '#88B73E' }} />
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.5rem' }}>
                            {group.childName}
                        </Typography>
                    </Box>

                    {/* Event Cards */}
                    <Stack spacing={1.75}>
                        {group.items.map((item) => (
                            <CartItemCard
                                key={item.id}
                                item={item}
                                onRemove={() => onRemoveItem(item.id)}
                                onExpired={onItemExpired}
                            />
                        ))}
                    </Stack>
                </Box>
            ))}

            {/* Remove All Button */}
            {items && items.length > 0 && (
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveShoppingCartIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    <Button
                        onClick={onRemoveAll}
                        color="error"
                        sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' }}
                    >
                        Remove All Items
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default CartItemsList;

